import xss from 'xss';

const baseWhiteList = {
    u: [],
    br: [],
    b: [],
    blockquote: [],
    s: [],
    i: [],
    ol: ['style'],
    ul: ['style'],
    li: ['class'],
    p: ['style'],
    sub: [],
    sup: [],
    div: ['style'],
    em: [],
    strong: [],
    span: ['style'],
    h1: ['style'],
    h2: ['style'],
    h3: ['style'],
    a: ['style', 'href', 'target'],
    video: ['controls', 'style'],
    source: ['src', 'type'],
    iframe: ['class', 'allowfullscreen', 'src', 'frameborder']
};

// The pixel the api appends to every sent body looks like
// `<img src="…/track.gif?uuid_campaign=…" class="sender-px">`. Two independent
// signals so that a change to either one still leaves it detected.
const SRC_ATTRIBUTE = /\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const SENDER_PX_CLASS = /\sclass\s*=\s*(?:"[^"]*\bsender-px\b|'[^']*\bsender-px\b)/i;

/**
 * Is this `<img>` tag panelfox's open-tracking pixel?
 *
 * Matched on the `src` (or the marker class) rather than anywhere in the tag: a
 * substring search over the whole tag also hits an externally hosted image that
 * merely mentions track.gif in its `alt` text, and silently dropped it.
 *
 * @param {string} html the raw source of a single img tag
 * @returns {boolean}
 */
function isTrackingPixel(html) {
    if (SENDER_PX_CLASS.test(html)) {
        return true;
    }

    const match = SRC_ATTRIBUTE.exec(html);
    const src = match ? match[1] || match[2] || match[3] || '' : '';

    return /\/track\.gif(\?|$)/i.test(src);
}

/**
 * Remove the open-tracking pixel from a full HTML email body.
 *
 * For a body rendered in an `<iframe srcDoc>` the xss filter is not an option: its
 * allowlist has no `table`/`tr`/`td`/`style`, and essentially every HTML email is
 * table-based, so filtering would collapse the layout. Dropping the one `<img>` that
 * must not be fetched — rendering it records a bogus "open" against the panelist — is
 * the narrow fix; the iframe stays sandboxed either way.
 *
 * @param {string} html a rendered email body
 * @returns {string} the same body without the tracking pixel
 */
export const stripTrackingPixel = html =>
    typeof html === 'string' ? html.replace(/<img\b[^>]*>/gi, tag => (isTrackingPixel(tag) ? '' : tag)) : html;

const myxss = new xss.FilterXSS({
    whiteList: baseWhiteList,
    onIgnoreTag: function(tag, html, options) {
        if (tag === 'img') {
            if (isTrackingPixel(html)) {
                // this is panelfox's tracking gif.. so just hide this
                return '';
            } else {
                return `[${tag}]`;
            }
        }
    }
});

export const emailXss = new xss.FilterXSS({
    whiteList: {
        ...baseWhiteList,
        // Add img to the whitelist with safe attributes
        img: ['src', 'alt', 'title', 'width', 'height', 'style']
    },
    // Custom URL check to prevent malicious URLs in src attributes
    safeAttrValue: function(tag, name, value, cssFilter) {
        // Additional check for image src attributes
        if (tag === 'img' && name === 'src') {
            // Only allow https protocols for images
            if (/^(https:\/\/|data:image\/)/i.test(value)) {
                return value;
            } else {
                return '';
            }
        }
        // For other attributes, use the default handler
        return xss.safeAttrValue(tag, name, value, cssFilter);
    },
    // onTag runs for whitelisted tags too, unlike onIgnoreTag. img IS whitelisted here,
    // so this is the only hook that can still drop panelfox's open-tracking pixel — and it
    // has to be dropped, because rendering it in-app would fetch track.gif and record a
    // bogus "open" against the recipient.
    onTag: function(tag, html, options) {
        if (tag === 'img' && isTrackingPixel(html)) {
            return '';
        }
    },
    onIgnoreTag: function(tag, html, options) {
        if (tag === 'img') {
            // Let img tags be processed by the whitelist now instead of converting them to [img]
            return null;
        }
    }
});

export default myxss;
